/**
 * O tour guiado das Novidades (spec 036, D4) — componente próprio, sem
 * dependência nova: escurece a tela, destaca o elemento `data-tour="…"` do
 * passo, rola até ele e põe ao lado um balão com ícone, título, texto, os
 * pontos de progresso e "Entendi".
 *
 * Regras:
 * - passo cuja âncora não aparece na tela é PULADO, e o tour segue; sem nenhum
 *   passo na tela, ele termina calado e não conta como feito (a pessoa não viu
 *   nada);
 * - "Entendi" no último passo, o X ou Esc terminam o tour e o marcam como
 *   feito para a pessoa (na API; nunca na aba "ver como");
 * - teclado: o foco vai para o "Entendi" a cada passo, Tab circula só dentro do
 *   balão, Esc fecha, e o foco volta para onde estava.
 *
 * Âncora DENTRO de um diálogo (o Novo ticket é um diálogo modal do Radix): o
 * tour é desenhado DENTRO do diálogo. Fora dele, o diálogo trataria o clique no
 * "Entendi" como clique de fora e se fecharia, e a trava de foco dele não
 * deixaria o teclado chegar ao balão.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Bell,
  FileText,
  Filter,
  KeyRound,
  List,
  Map as MapIcon,
  RefreshCw,
  Search,
  Sparkles,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { NewsText } from './news-content';
import { useNews } from './news-queries';
import { nextShownStep, type NewsAudience, type NewsTour, type NewsTourIcon } from './news-model';

// ── o tour ativo (um por aba) ───────────────────────────────────────────────

interface ActiveTour {
  tour: NewsTour;
  /** muda a cada início: o mesmo tour começado de novo é outro */
  run: number;
}

let active: ActiveTour | null = null;
let runs = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** começa o tour (o painel chama depois de navegar para a tela dele) */
export function startNewsTour(tour: NewsTour): void {
  active = { tour, run: ++runs };
  emit();
}

function endNewsTour(): void {
  active = null;
  emit();
}

function useActiveTour(): ActiveTour | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => active
  );
}

// ── âncoras ─────────────────────────────────────────────────────────────────

/** quanto esperar pela âncora: a primeira espera a tela carregar; as outras já estão nela */
const FIRST_STEP_WAIT_MS = 4000;
const NEXT_STEP_WAIT_MS = 700;
const POLL_MS = 100;

function findAnchor(anchor: string): HTMLElement | null {
  const el = document.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);
  if (!el || !el.isConnected) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 || r.height > 0 ? el : null;
}

const ICONS: Record<NewsTourIcon, LucideIcon> = {
  search: Search,
  filter: Filter,
  key: KeyRound,
  users: Users,
  map: MapIcon,
  list: List,
  file: FileText,
  sparkles: Sparkles,
  refresh: RefreshCw,
  bell: Bell,
};

// ── a camada ────────────────────────────────────────────────────────────────

/** Monte uma vez por layout (equipe e portal). */
export function NewsTourHost({ audience }: { audience: NewsAudience }) {
  const current = useActiveTour();
  if (!current) return null;
  return <TourRunner key={current.run} tour={current.tour} audience={audience} />;
}

interface Layout {
  position: 'fixed' | 'absolute';
  spot: { top: number; left: number; width: number; height: number };
  pop: { top: number; left: number };
  cover: { width: number; height: number } | null;
}

const SPOT_PAD = 6;
const GAP = 12;
const EDGE = 8;
const POP_WIDTH = 320;

/**
 * Onde desenhar, nas coordenadas do recipiente: a viewport (fixed) ou o
 * diálogo que contém a âncora (absolute — o conteúdo do diálogo é `fixed` e
 * rola por dentro, então a posição acompanha a rolagem dele).
 */
function computeLayout(el: HTMLElement, container: HTMLElement | null, popHeight: number): Layout {
  const r = el.getBoundingClientRect();
  let position: Layout['position'] = 'fixed';
  let dx = 0;
  let dy = 0;
  let view = { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
  let cover: Layout['cover'] = null;
  if (container) {
    const c = container.getBoundingClientRect();
    position = 'absolute';
    dx = -c.left - container.clientLeft + container.scrollLeft;
    dy = -c.top - container.clientTop + container.scrollTop;
    view = {
      top: container.scrollTop,
      left: container.scrollLeft,
      width: container.clientWidth,
      height: container.clientHeight,
    };
    cover = { width: container.scrollWidth, height: container.scrollHeight };
  }
  const spot = {
    top: r.top + dy - SPOT_PAD,
    left: r.left + dx - SPOT_PAD,
    width: r.width + SPOT_PAD * 2,
    height: r.height + SPOT_PAD * 2,
  };
  const width = Math.min(POP_WIDTH, view.width - EDGE * 2);
  const below = spot.top + spot.height + GAP;
  const above = spot.top - GAP - popHeight;
  let top = below;
  if (below + popHeight > view.top + view.height - EDGE && above >= view.top + EDGE) top = above;
  top = Math.max(view.top + EDGE, Math.min(top, view.top + view.height - EDGE - popHeight));
  const left = Math.max(view.left + EDGE, Math.min(spot.left, view.left + view.width - EDGE - width));
  return { position, spot, pop: { top, left }, cover };
}

const sameLayout = (a: Layout | null, b: Layout) =>
  a !== null &&
  a.position === b.position &&
  Math.abs(a.spot.top - b.spot.top) < 0.5 &&
  Math.abs(a.spot.left - b.spot.left) < 0.5 &&
  Math.abs(a.spot.width - b.spot.width) < 0.5 &&
  Math.abs(a.spot.height - b.spot.height) < 0.5 &&
  Math.abs(a.pop.top - b.pop.top) < 0.5 &&
  Math.abs(a.pop.left - b.pop.left) < 0.5 &&
  a.cover?.width === b.cover?.width &&
  a.cover?.height === b.cover?.height;

function TourRunner({ tour, audience }: { tour: NewsTour; audience: NewsAudience }) {
  const { vars, markTourDone } = useNews(audience);
  const steps = tour.steps;
  /** o passo procurado (`searching`) ou mostrado */
  const [stepIndex, setStepIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [layout, setLayout] = useState<Layout | null>(null);
  const shownRef = useRef(0);
  const popRef = useRef<HTMLDivElement>(null);
  const okRef = useRef<HTMLButtonElement>(null);
  const restoreRef = useRef<Element | null>(document.activeElement);
  const titleId = useId();
  const textId = useId();

  const finish = useCallback(() => {
    // "Entendi" no último, X ou Esc: feito — se a pessoa viu algum passo
    if (shownRef.current > 0) markTourDone(tour.id);
    endNewsTour();
    const back = restoreRef.current;
    if (back instanceof HTMLElement && back.isConnected) back.focus({ preventScroll: true });
  }, [markTourDone, tour.id]);

  // procura a âncora do passo; sem ela no prazo, pula para o próximo
  useEffect(() => {
    if (anchorEl) return;
    let cancelled = false;
    const deadline = Date.now() + (shownRef.current === 0 ? FIRST_STEP_WAIT_MS : NEXT_STEP_WAIT_MS);
    const tick = () => {
      if (cancelled) return;
      const step = steps[stepIndex];
      if (!step) {
        // nenhum passo sobrou: feito, se a pessoa viu algum
        if (shownRef.current > 0) markTourDone(tour.id);
        endNewsTour();
        return;
      }
      const el = findAnchor(step.anchor);
      if (el) {
        shownRef.current += 1;
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        setAnchorEl(el);
        return;
      }
      if (Date.now() >= deadline) {
        const next = nextShownStep(steps, stepIndex + 1, (a) => findAnchor(a) !== null);
        // ninguém na tela agora: tenta o seguinte, com o prazo dele
        setStepIndex(next ?? stepIndex + 1);
        return;
      }
      timer = window.setTimeout(tick, POLL_MS);
    };
    let timer = window.setTimeout(tick, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [anchorEl, stepIndex, steps, markTourDone, tour.id]);

  const container = anchorEl?.closest<HTMLElement>('[role="dialog"]') ?? null;

  // acompanha a âncora quadro a quadro (rolagem, diálogo animando, tela mudando)
  useLayoutEffect(() => {
    if (!anchorEl) return;
    let frame = 0;
    const loop = () => {
      if (!anchorEl.isConnected) {
        // a âncora saiu da tela: segue para o próximo passo
        setLayout(null);
        setAnchorEl(null);
        setStepIndex((i) => i + 1);
        return;
      }
      const next = computeLayout(anchorEl, container, popRef.current?.offsetHeight ?? 160);
      setLayout((prev) => (sameLayout(prev, next) ? prev : next));
      frame = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(frame);
  }, [anchorEl, container]);

  // o foco vai para o "Entendi" a cada passo — quando o passo muda, não a cada quadro
  const placed = layout !== null;
  useEffect(() => {
    if (anchorEl && placed) okRef.current?.focus({ preventScroll: true });
  }, [anchorEl, placed]);

  // Esc fecha — na fase de captura da JANELA, antes do Esc do diálogo do Radix
  // (que é no documento): sem isso, o Esc fecharia o Novo ticket junto
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      finish();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [finish]);

  const next = () => {
    const following = stepIndex + 1;
    setLayout(null);
    setAnchorEl(null);
    if (following >= steps.length) finish();
    else setStepIndex(following);
  };

  // Tab circula dentro do balão
  const onPopKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !popRef.current) return;
    const items = [...popRef.current.querySelectorAll<HTMLElement>('button:not([disabled])')];
    if (items.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    const at = items.indexOf(document.activeElement as HTMLElement);
    const to = e.shiftKey ? (at <= 0 ? items.length - 1 : at - 1) : at === items.length - 1 ? 0 : at + 1;
    items[to]!.focus();
  };

  const step = steps[stepIndex];
  if (!anchorEl || !layout || !step) return null;
  const Icon = step.icon ? ICONS[step.icon] : Sparkles;

  const layer = (
    <div data-news-tour="" className="contents">
      {/* pega os cliques fora do balão: o tour não deixa a tela de trás reagir */}
      <div
        aria-hidden
        className="z-[60]"
        style={
          layout.cover
            ? { position: 'absolute', top: 0, left: 0, width: layout.cover.width, height: layout.cover.height }
            : { position: 'fixed', inset: 0 }
        }
        onMouseDown={(e) => e.preventDefault()}
      />
      {/* o destaque: o anel em volta da âncora, e a sombra enorme escurece o resto
          (as duas no MESMO box-shadow: o ring do Tailwind também é box-shadow, e o
          estilo em linha o apagaria) */}
      <div
        aria-hidden
        className="pointer-events-none z-[61] rounded-lg transition-[top,left,width,height] duration-150"
        style={{
          position: layout.position,
          ...layout.spot,
          boxShadow: '0 0 0 2px var(--primary), 0 0 0 9999px rgba(15, 23, 42, 0.55)',
        }}
      />
      <div
        ref={popRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={textId}
        onKeyDown={onPopKeyDown}
        className="z-[62] rounded-xl border bg-popover p-4 text-popover-foreground shadow-xl"
        style={{ position: layout.position, top: layout.pop.top, left: layout.pop.left, width: POP_WIDTH, maxWidth: 'calc(100vw - 16px)' }}
      >
        <button
          type="button"
          onClick={finish}
          aria-label="Fechar o tour"
          className="absolute top-2.5 right-2.5 flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon className="size-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 id={titleId} className="text-sm font-semibold text-foreground">
              <NewsText text={step.title} vars={vars} />
            </h2>
            <p id={textId} className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
              <NewsText text={step.text} vars={vars} />
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5" aria-hidden>
            {steps.map((s, i) => (
              <span
                key={s.anchor}
                className={cn('h-1.5 rounded-full', i === stepIndex ? 'w-5 bg-primary' : 'w-1.5 bg-muted-foreground/30')}
              />
            ))}
          </div>
          <span className="sr-only" aria-live="polite">{`Passo ${stepIndex + 1} de ${steps.length}`}</span>
          <Button ref={okRef} size="sm" onClick={next}>
            Entendi
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(layer, container ?? document.body);
}
